import React from "react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { AlertCircle, ChevronRight } from "lucide-react";
import { JSONObject } from "@/components/jsonSchemaForm/types";

interface UnconfirmedPartsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  unconfirmedParts: Array<{ index: number; item: JSONObject }>;
  onNavigateToPart: (index: number) => void;
}

/**
 * UnconfirmedPartsDialog displays a list of parts that have not been confirmed
 * and allows users to navigate to them for review.
 */
export const UnconfirmedPartsDialog: React.FC<UnconfirmedPartsDialogProps> = ({
  open,
  onOpenChange,
  unconfirmedParts,
  onNavigateToPart,
}) => {
  const getPartLabel = (item: JSONObject, index: number) => {
    const partNumber =
      typeof item.partNumber === "string" ? item.partNumber : undefined;
    const partDescription =
      typeof item.partDescription === "string"
        ? item.partDescription
        : undefined;
    const type = typeof item.type === "string" ? item.type : undefined;

    const parts: string[] = [`Part ${index + 1}`];
    if (partNumber) {
      parts.push(partNumber);
    }
    return {
      primary: parts.join(": "),
      secondary: type,
      description: partDescription,
    };
  };

  const handleNavigate = (index: number) => {
    onOpenChange(false);
    onNavigateToPart(index);
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-yellow-600" />
            Cannot Approve - Parts Not Confirmed
          </AlertDialogTitle>
          <AlertDialogDescription>
            The following parts have not been confirmed. Please review and
            confirm each part before approving:
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="flex-1 overflow-auto py-4 space-y-2">
          {unconfirmedParts.map(({ index, item }) => {
            const labels = getPartLabel(item, index);
            return (
              <button
                key={index}
                onClick={() => handleNavigate(index)}
                className="w-full text-left p-3 rounded-md border bg-background hover:border-red-500 hover:shadow-md transition-all flex items-start gap-3 group"
              >
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm group-hover:text-red-600">
                    {labels.primary}
                  </div>
                  {labels.secondary && (
                    <div className="text-xs text-muted-foreground mt-1">
                      {labels.secondary}
                    </div>
                  )}
                  {labels.description && (
                    <div className="text-xs text-muted-foreground mt-1 line-clamp-2">
                      {labels.description}
                    </div>
                  )}
                </div>
                <ChevronRight className="h-5 w-5 text-red-500 opacity-0 group-hover:opacity-100 transition-all group-hover:translate-x-1 flex-shrink-0 mt-0.5" />
              </button>
            );
          })}
        </div>

        <AlertDialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
